/****************************************************************
 * jotai state and atoms for the accounts listing
 ***************************************************************/

import { atom, useAtom, useSetAtom, useAtomValue } from 'jotai';
import { atomWithReset, atomWithStorage, useResetAtom } from 'jotai/utils';
import callApi from 'lib/callApi';
import { companyIdAtom } from 'views/state';
import { DEFAULT_PAGE_SIZE } from 'views/types';

import { RepositoryFilterOptions } from '../helper';

export const DEFAULT_REPOSITORY_LISTING_COLUMNS = [
  'repo_branch',
  'account',
  'status',
  'vulnerabilities',
  'last_commit_date',
  'scan__last_sync_completed_at',
  'actions',
];
export const enabledColumnKeysAtom = atomWithStorage(
  'sast_repos_enabled_column_keys',
  DEFAULT_REPOSITORY_LISTING_COLUMNS,
);

export const selectedRowIdsAtom = atomWithReset<number[]>([]);
export const forceReloadRepoDataAtom = atom<string>('');

export const paginationConfigAtom = atomWithStorage(
  'sast_repositories_pagination_config',
  {
    page_size: DEFAULT_PAGE_SIZE,
    page: 1,
  },
);
export const orderbyAtom = atomWithReset<string>('updated_at');
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('desc');

// Additional filtering
export const selectedAccountIdListAtom = atomWithReset<number[]>([]);
export const selectedHostTypeListAtom = atomWithReset<string[]>([]);
export const selectedStateFiltersListAtom = atomWithReset<string[]>([
  RepositoryFilterOptions.Active,
]);

// Store the combined count of vulnerabilities and bugs for each checked repository
export const checkedRepoVulnAndBugsMapAtom = atom<Record<number, number>>({});

// Calculate total vulnerabilities and bugs across all checked repositories
export const totalVulnAndBugsForCheckedReposAtom = atom(async (get) => {
  const checkedRepoIds = get(selectedRowIdsAtom);
  const vulnAndBugsMap = get(checkedRepoVulnAndBugsMapAtom);
  let total = 0;

  if (checkedRepoIds?.length > 0) {
    const allRepos = get(repositoriesDataAtom);

    // Update counts with any new repositories from current page
    const currentPageRepos = allRepos?.results?.filter((repo) =>
      checkedRepoIds.includes(repo.id),
    );

    // Calculate total using both stored and current page values
    checkedRepoIds.forEach((repoId) => {
      // Use stored value if available, otherwise 0
      total += vulnAndBugsMap[repoId] || 0;
    });

    // Update totals with any new values from current page
    currentPageRepos?.forEach((repo) => {
      const totalIssues =
        (repo.scan?.synced_vuln_count || 0) +
        (repo.scan?.synced_bugs_count || 0);
      if (totalIssues !== vulnAndBugsMap[repo.id]) {
        total = total - (vulnAndBugsMap[repo.id] || 0) + totalIssues;
      }
    });
  }
  return total;
});

interface Insights {
  status_breakdown: {
    active: number;
    inactive: number;
    error: number;
    disconnected: number;
    archived: number;
  };
  severity_breakdown: {
    bugs: {
      urgent: number;
      critical: number;
      serious: number;
      medium: number;
      minimal: number;
    };
    vulnerabilities: {
      urgent: number;
      critical: number;
      serious: number;
      medium: number;
      minimal: number;
    };
  };
  repo_type_breakdown: {
    azure: number;
    bitbucket: number;
    github: number;
    gitlab: number;
  };
  accounts_count: number;
  total_vulnerabilities: number;
  total_bugs: number;
}

export const repositoriesDataAtom = atom({
  results: [] as any[],
  count: 0,
  loading: false,
  insights: {} as Insights,
});

export const forceRefreshStatusAtom = atom<string>('');
export const forceRerenderUIAtom = atom<string>('');
export const repositoriesStatusDataAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const forceRefresh = get(forceRefreshStatusAtom);
  const repositoriesData = get(repositoriesDataAtom);

  try {
    // only load on force refresh
    if (
      companyId &&
      companyId !== -1 &&
      repositoriesData?.results &&
      forceRefresh
    ) {
      const params = {
        company_id: companyId,
        repo_ids: repositoriesData?.results.map((i) => i.id).join(','),
      };

      const responseData = await callApi(
        'sast/repositories/status',
        'GET',
        params,
      );

      return responseData?.data;
    }
  } catch (error) {}
  return undefined;
});

const DateFilterKeys = [
  'created_at',
  'updated_at',
  'deactivated_at',
  'reactivated_at',
  'last_commit_date',
];
export const filterValuesAtom = atomWithReset<Record<string, any>>({
  state_filters: ['active'],
});
export const isQuerystringFiltersInitializedAtom =
  atomWithReset<boolean>(false);
export const repositoryDataQueryParamsAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const paginationConfig = get(paginationConfigAtom);
  const pageSize = paginationConfig.page_size;
  const page = paginationConfig.page;
  const orderBy = get(orderbyAtom);
  const order = get(sortOrderAtom);
  // Add id to orderBy if not present, then apply sort order
  const orderByWithId = orderBy
    .split(',')
    .some((field) => field.trim() === 'id' || field.trim() === '-id')
    ? orderBy
    : `${orderBy},id`;
  const ordering = orderByWithId
    .split(',')
    .map(
      (field) =>
        `${order === 'desc' && !field.startsWith('-') ? '-' : ''}${field}`,
    )
    .join(',');
  const filterValues = get(filterValuesAtom);
  const _repoRefresh = get(forceReloadRepoDataAtom);

  if (companyId && companyId !== -1) {
    const params = {
      company_id: companyId.toString(),
      page: page,
      page_size: pageSize,
      ordering: ordering,
    };

    Object.entries(filterValues).forEach(([key, value]) => {
      if (value && value.length > 0) {
        if (DateFilterKeys.includes(key)) {
          const fromDate = value[0]?.from;
          const toDate = value[0]?.to;
          if (fromDate instanceof Date) {
            params[`${key}_after`] = fromDate.toISOString();
          } else {
            console.error({
              msg: 'fromDate is not a valid Date object:',
              fromDate,
            });
          }
          if (toDate) params[`${key}_before`] = toDate?.toISOString();
        } else {
          params[key] = value.join(',');
        }
      }
    });

    return params;
  }
});

export const useOnFilterChangeResetAtoms = () => {
  const setSelectedRowIds = useSetAtom(selectedRowIdsAtom);
  const setPaginationConfig = useSetAtom(paginationConfigAtom);

  return () => {
    setSelectedRowIds([]);
    setPaginationConfig((prev) => ({ ...prev, page: 1 }));
  };
};

export const useRepositoryListFilterValue = <T = string>(
  key: string,
  defaultValue: T = '' as unknown as T,
): T => {
  const filterValues = useAtomValue(filterValuesAtom);
  return (filterValues?.[key] ?? defaultValue) as T;
};

/**
 * reset the repository page state
 */
export function useRepositoryResetAtoms() {
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const resetOrderbyAtom = useResetAtom(orderbyAtom);
  const resetOrderAtom = useResetAtom(sortOrderAtom);
  const setReposForceRefresh = useSetAtom(forceReloadRepoDataAtom);
  const resetSelectedAccountIdList = useResetAtom(selectedAccountIdListAtom);
  const resetSelectedHostTypeList = useResetAtom(selectedHostTypeListAtom);
  const resetSelectedStateFiltersList = useResetAtom(
    selectedStateFiltersListAtom,
  );
  const resetSelectedRowIds = useResetAtom(selectedRowIdsAtom);
  const resetFilterValuesAtom = useResetAtom(filterValuesAtom);

  const resetAtoms = () => {
    // Only reset page number, preserve page size
    setPaginationConfig({
      ...paginationConfig,
      page: 1,
    });
    resetOrderbyAtom();
    resetOrderAtom();
    setReposForceRefresh(Math.random().toString());
    resetSelectedAccountIdList();
    resetSelectedHostTypeList();
    resetSelectedStateFiltersList();
    resetSelectedRowIds();
    resetFilterValuesAtom();
  };

  return resetAtoms;
}
